import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { screenAppAPIClient } from '@/lib/baseAxios';
import { pushModal } from '@/features/globalModals/globalModalsSlice';
import { RootState } from '@/reducers';
import { FolderType, FileType, Member } from '@/types';
import {
  SPECIAL_FOLDER_IDS,
  isSpecialFolder,
  MAX_VISIBLE_AVATARS,
} from '@/constants/folders';
import FolderAccessModal from '@/features/library/folders/actions/FolderAccessModal';
import { updateFolder } from '@/features/library/librarySlice';
import { ShareDialog } from './ShareDialog';

interface AccessAvatarListProps {
  item?: FolderType | FileType;
  folder?: FolderType | null;
  className?: string;
  size?: 'sm' | 'md';
  hideShareDialog?: boolean;
  onAddClick?: () => void;
}

/**
 * Component that displays a list of avatars for users who have access to a folder or video
 */
export const AccessAvatarList: React.FC<AccessAvatarListProps> = ({
  item,
  folder: propFolder,
  className,
  size = 'md',
  hideShareDialog = false,
  onAddClick,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { folderId } = useParams<{
    folderId?: string;
  }>();

  const {
    teamMembers,
    teamOwnerId,
    selectedTeamId,
    folders,
    selectedFolder,
    selectedVideoFile,
    currentUser,
  } = useSelector((state: RootState) => ({
    teamMembers: state.team.selectedTeam?.teamSpaceMembers || [],
    teamOwnerId: state.team.selectedTeam?.ownerId,
    selectedTeamId: state.team.selectedTeam?.teamId,
    folders: state.library.folders?.data || [],
    selectedFolder: state.library.selectedFolder,
    selectedVideoFile: state.selectedVideo?.file,
    currentUser: state.settings.user,
  }));

  // Check if current user is team owner or admin
  const isTeamOwnerOrAdmin = React.useMemo(() => {
    if (!currentUser) return false;

    // Check if user is the team owner
    if (currentUser._id === teamOwnerId) return true;

    // Check if user is an admin
    const currentUserMember = teamMembers.find(
      (m) => m._id === currentUser._id,
    );
    if (currentUserMember) {
      const { role } = currentUserMember;
      return role === 'Admin' || role === 'Owner' || role === 'OWNER';
    }

    return false;
  }, [currentUser, teamOwnerId, teamMembers]);

  // Type guard to check if an entity is a folder
  const isFolderType = (
    entity: FolderType | FileType,
  ): entity is FolderType => {
    return entity.type === 'Folder';
  };

  // Determine the folder to use for permissions
  const folder = React.useMemo(() => {
    if (propFolder) {
      return propFolder;
    }

    if (!item) {
      return null;
    }

    // Check if this is a folder or file using type guard
    if (isFolderType(item)) {
      return item;
    }

    // For files, try to find the parent folder
    const file = item as FileType;
    const fileParentId = file.parentId || selectedVideoFile?.parentId;

    // Try 1: Use parentId from file or selectedVideoFile
    if (fileParentId) {
      let foundFolder = folders.find((f) => f._id === fileParentId);
      if (!foundFolder && selectedFolder?._id === fileParentId) {
        foundFolder = selectedFolder;
      }
      if (foundFolder) {
        return foundFolder;
      }
    }

    // Try 2: Use folderId from URL params (for selected video view) - but skip special folders
    if (folderId && !isSpecialFolder(folderId)) {
      let foundFolder = folders.find((f) => f._id === folderId);
      if (!foundFolder && selectedFolder?._id === folderId) {
        foundFolder = selectedFolder;
      }
      if (foundFolder) {
        return foundFolder;
      }
    }

    // Try 3: Use selectedFolder directly as last resort (skip special folders)
    if (selectedFolder && !isSpecialFolder(selectedFolder._id)) {
      return selectedFolder;
    }

    return null;
  }, [item, propFolder, folders, selectedFolder, folderId, selectedVideoFile]);

  // State for forcing re-renders
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Determine users with access
  const usersWithAccess = React.useMemo(() => {
    // In team member context (home/root), show ALL team members
    if (!item && !folder) {
      return teamMembers;
    }

    // For private files (no folder context), only show the owner
    if (item && !isFolderType(item) && !folder) {
      const file = item as FileType;
      if (!file.parentId) {
        return file.ownerId
          ? teamMembers.filter((m) => m._id === file.ownerId)
          : [];
      }
    }

    // If folder has null permissions, all team members have access
    if (folder?.permissions === null || folder?.permissions === undefined) {
      return teamMembers;
    }

    // If folder has explicit permissions array
    if (folder && Array.isArray(folder.permissions)) {
      // Include owner + users in permissions array
      const permittedUserIds = new Set(folder.permissions.map((p) => p.userId));

      if (folder.ownerId) {
        permittedUserIds.add(folder.ownerId);
      }

      return teamMembers.filter((member) => permittedUserIds.has(member._id));
    }

    // Default: return empty array
    return [];
  }, [item, folder, teamMembers, folder?.permissions, refreshKey]); // Added folder?.permissions to deps

  const avatarSize = size === 'sm' ? 'h-10 w-10' : 'h-12 w-12';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return '?';
  };

  // Get current user as a member object from team members
  const currentUserAsMember = React.useMemo(() => {
    if (currentUser?._id) {
      // Try to find user in team members first
      const foundUser = teamMembers.find((m) => m._id === currentUser._id);

      if (foundUser) {
        return foundUser;
      }

      // If not found in team members (e.g., owner might not be in the list),
      // create a Member object from currentUser
      return {
        _id: currentUser._id,
        name: currentUser.name,
        email: currentUser.email,
        picture: currentUser.picture,
        role: currentUser.role,
      } as Member;
    }
    return null;
  }, [teamMembers, currentUser]);

  // Filter out current user from the list and show max others
  const otherUsers = usersWithAccess.filter(
    (u) => u._id !== currentUser?._id && u._id !== currentUserAsMember?._id,
  );
  const visibleOthers = otherUsers.slice(0, MAX_VISIBLE_AVATARS);
  const remainingCount = Math.max(0, otherUsers.length - MAX_VISIBLE_AVATARS);

  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);
  const [isManageAccessOpen, setIsManageAccessOpen] = React.useState(false);
  const [confirmRemoveUser, setConfirmRemoveUser] =
    React.useState<Member | null>(null);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [undoData, setUndoData] = React.useState<{
    userId: string;
    type: 'folder';
    resourceId: string;
    previousPermissions?: any[];
    permissionToRestore?: any;
  } | null>(null);
  const [isFolderAccessModalOpen, setIsFolderAccessModalOpen] =
    React.useState(false);

  // Check if we're in a special library view (recents, private, etc.)
  const isSpecialLibraryView =
    !folder &&
    !item &&
    (folderId === SPECIAL_FOLDER_IDS.RECENTS ||
      folderId === SPECIAL_FOLDER_IDS.PRIVATE ||
      folderId === SPECIAL_FOLDER_IDS.SHARED ||
      folderId === SPECIAL_FOLDER_IDS.TRASH);

  // Refresh folder data when manage access dialog closes
  React.useEffect(() => {
    if (!isManageAccessOpen && folder) {
      // Dispatch action to refetch folder permissions
      // This ensures the avatar list updates with latest data
      // TODO: Replace with your actual refresh logic
      // dispatch(refreshFolderPermissions(folder._id));
    }
  }, [isManageAccessOpen, folder]);

  // Refresh when FolderAccessModal closes
  React.useEffect(() => {
    if (!isFolderAccessModalOpen && folder) {
      // Force a re-render by triggering a folder data refresh
      // This ensures avatars update after permissions change
      window.dispatchEvent(
        new CustomEvent('folderPermissionsChanged', {
          detail: { folderId: folder._id },
        }),
      );
    }
  }, [isFolderAccessModalOpen, folder?._id]);

  const handleAddClick = () => {
    if (onAddClick) {
      // If parent provided a callback, use it
      onAddClick();
    } else if (isSpecialLibraryView) {
      // In special library views (recents, private, etc.), open the team invite modal
      dispatch(pushModal({ type: 'invite-by-link-modal' }));
    } else {
      // Otherwise, open our own share dialog
      setIsShareDialogOpen(true);
    }
  };

  const handleUndo = async () => {
    if (!undoData) return;

    try {
      if (undoData.type === 'folder' && undoData.permissionToRestore) {
        // We need to add the user back to the CURRENT permissions list, not restore an old list
        // This prevents overwriting other changes that happened since the removal
        const currentPermissions = folder?.permissions || [];

        // Check if user is already in permissions (shouldn't happen, but safety first)
        const isAlreadyThere = currentPermissions.some(
          (p) => p.userId === undoData.userId,
        );

        // Construct the new permissions list
        const restoredPermissions = isAlreadyThere
          ? currentPermissions
          : [...currentPermissions, undoData.permissionToRestore];

        await screenAppAPIClient.files.updateFolderPermissions(
          undoData.resourceId,
          restoredPermissions,
        );

        // Update Redux state with the new merged list
        if (folder && folder._id === undoData.resourceId) {
          const updatedFolder = {
            ...folder,
            permissions: restoredPermissions,
          };
          dispatch(updateFolder(updatedFolder));
        }

        toast.success('Undone');

        // Force re-render not needed
        // setRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error undoing:', error);
      toast.error('Failed to undo');
    } finally {
      setUndoData(null);
    }
  };

  const removeFolderPermission = async (user: Member) => {
    if (!folder) return;

    // Prevent removing owner
    if (user._id === folder.ownerId) {
      toast.error('Cannot remove the owner');
      return;
    }

    try {
      setIsRemoving(true);

      // Find the specific permission object for this user to store for undo
      // We look in the current folder permissions
      const permissionToRestore = folder.permissions?.find(
        (p) => p.userId === user._id,
      );

      // If we can't find it (maybe they were just added locally?), construct a default one
      // But usually it should be there.
      const fallbackPermission = {
        userId: user._id,
        role: 'viewer', // Default fallback
      };

      // Call API
      const response = await screenAppAPIClient.files.removeFolderPermission(
        folder._id,
        user._id,
      );

      if (response.success) {
        // Optimistically update folder permissions by dispatching an action
        // Do NOT mutate the folder object directly as it is read-only Redux state
        if (folder.permissions && Array.isArray(folder.permissions)) {
          const newPermissions = folder.permissions.filter(
            (p) => p.userId !== user._id,
          );
          const updatedFolder = { ...folder, permissions: newPermissions };
          dispatch(updateFolder(updatedFolder));
        }

        setUndoData({
          userId: user._id,
          type: 'folder',
          resourceId: folder._id,
          previousPermissions: [], // No longer used, but keeping for type compatibility if needed
          permissionToRestore: permissionToRestore || fallbackPermission,
        });

        // Show success toast - ARROW FUNCTION for onClick
        toast.success(`Removed ${user.name || user.email}`, {
          action: {
            label: 'Undo',
            onClick: () => handleUndo(),
          },
          duration: 5000,
        });

        // Force re-render not needed if we update Redux state correctly
        // setRefreshKey(prev => prev + 1);

        // Keep dialog OPEN so user can remove more people
      }
    } catch (error) {
      console.error('Error removing folder permission:', error);
      toast.error(`Failed to remove ${user.name || user.email}`);
    } finally {
      setIsRemoving(false);
    }
  };

  // Determine if we're in team member context vs file/folder context
  const isTeamMemberContext = React.useMemo(() => {
    return !item && !folder;
  }, [item, folder]);

  const handleRemoveAccess = async (user: Member) => {
    // Team member removal
    if (isTeamMemberContext) {
      setConfirmRemoveUser(user);
      return;
    }

    // Check if folder has team-wide access (null or undefined permissions)
    if (folder?.permissions === null || folder?.permissions === undefined) {
      // Open Folder Access dialog to convert to restricted access
      setIsFolderAccessModalOpen(true);
      setIsManageAccessOpen(false);
      return;
    }

    // Folder with explicit permissions - remove user
    if (folder && Array.isArray(folder.permissions)) {
      await removeFolderPermission(user);
    }
  };

  const handleConfirmTeamRemoval = async () => {
    if (!confirmRemoveUser) return;

    const user = confirmRemoveUser;

    try {
      setIsRemoving(true);

      // Get the actual team ID from Redux state
      // You may need to adjust this based on your team state structure
      const teamId = selectedTeamId;

      if (!teamId) {
        toast.error('Team ID not found');
        return;
      }

      await screenAppAPIClient.teams.removeTeamMember({
        teamId,
        userId: user._id,
      });

      toast.success(`${user.name || user.email} removed from team`);
      setConfirmRemoveUser(null);
      setIsManageAccessOpen(false);
    } catch (error) {
      console.error('Error removing team member:', error);
      toast.error(`Failed to remove team member`);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <TooltipProvider>
      <div className={cn('flex items-center -space-x-4', className)}>
        {/* Always show current user first */}
        {currentUserAsMember && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Avatar className={cn(avatarSize, 'border-2 border-background')}>
                {currentUserAsMember.picture ? (
                  <AvatarImage
                    src={currentUserAsMember.picture}
                    alt={currentUserAsMember.name || currentUserAsMember.email}
                  />
                ) : null}
                <AvatarFallback className={cn(textSize)}>
                  {getInitials(
                    currentUserAsMember.name,
                    currentUserAsMember.email,
                  )}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-0.5">
                <p className="font-medium">
                  {currentUserAsMember.name || t('common.unknown')}
                </p>
                <p className="text-xs opacity-80">
                  {currentUserAsMember.email}
                </p>
                <p className="text-xs opacity-80">{t('common.you')}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Show other users with access */}
        {visibleOthers.map((user) => (
          <Tooltip key={user._id}>
            <TooltipTrigger asChild>
              <Avatar
                className={cn(
                  avatarSize,
                  'border-2 border-background',
                  isTeamOwnerOrAdmin && 'cursor-pointer',
                )}
                onClick={
                  isTeamOwnerOrAdmin
                    ? () => setIsManageAccessOpen(true)
                    : undefined
                }
                role={isTeamOwnerOrAdmin ? 'button' : undefined}
                tabIndex={isTeamOwnerOrAdmin ? 0 : undefined}
                onKeyDown={
                  isTeamOwnerOrAdmin
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsManageAccessOpen(true);
                        }
                      }
                    : undefined
                }
              >
                {user.picture ? (
                  <AvatarImage
                    src={user.picture}
                    alt={user.name || user.email}
                  />
                ) : null}
                <AvatarFallback className={cn(textSize)}>
                  {getInitials(user.name, user.email)}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-0.5">
                <p className="font-medium">
                  {user.name || t('common.unknown')}
                </p>
                <p className="text-xs opacity-80">{user.email}</p>
                {user._id === teamOwnerId && (
                  <p className="text-xs opacity-80">{t('common.teamOwner')}</p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        ))}

        {/* Show +N if there are more users - clicking opens manage access dialog */}
        {remainingCount > 0 && (
          <Avatar
            className={cn(
              avatarSize,
              'border-2 border-background',
              isTeamOwnerOrAdmin && 'cursor-pointer',
            )}
            onClick={
              isTeamOwnerOrAdmin ? () => setIsManageAccessOpen(true) : undefined
            }
            role={isTeamOwnerOrAdmin ? 'button' : undefined}
            tabIndex={isTeamOwnerOrAdmin ? 0 : undefined}
            onKeyDown={
              isTeamOwnerOrAdmin
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsManageAccessOpen(true);
                    }
                  }
                : undefined
            }
          >
            <AvatarFallback
              className={cn(
                textSize,
                'bg-muted text-muted-foreground font-medium',
              )}
            >
              +{remainingCount}
            </AvatarFallback>
          </Avatar>
        )}

        {/* Add button on the right with higher z-index - opens share dialog directly */}
        {/* Only show for team owners and admins */}
        {isTeamOwnerOrAdmin && (
          <Avatar
            className={cn(
              avatarSize,
              'border-2 border-background cursor-pointer transition-all hover:scale-105 active:scale-95 relative z-10',
            )}
            onClick={handleAddClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleAddClick();
              }
            }}
            aria-label={t('selectedVideo.actions.modals.shareDialog.addPeople')}
          >
            <AvatarFallback className="bg-muted text-muted-foreground font-medium text-lg hover:bg-accent transition-colors">
              +
            </AvatarFallback>
          </Avatar>
        )}
      </div>

      {/* Share Dialog - "+" button opens detailed view directly */}
      {!hideShareDialog && (
        <ShareDialog
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          startInDetailedView
          folder={folder}
          file={item && !isFolderType(item) ? item : undefined}
        />
      )}

      {/* Manage Access Dialog - placeholder */}
      <Dialog open={isManageAccessOpen} onOpenChange={setIsManageAccessOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('access.manage.title', 'Manage access')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-0">
            {/* Current user (owner) at the top */}
            {currentUserAsMember && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    {currentUserAsMember.picture ? (
                      <AvatarImage
                        src={currentUserAsMember.picture}
                        alt={
                          currentUserAsMember.name || currentUserAsMember.email
                        }
                      />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {getInitials(
                        currentUserAsMember.name,
                        currentUserAsMember.email,
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {currentUserAsMember.name || t('common.unknown')}
                      <span className="text-muted-foreground font-normal">
                        {' '}
                        ({t('common.owner', 'Owner')})
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {currentUserAsMember.email}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {/* Other users with access */}
            {otherUsers.map((user) => (
              <div
                key={user._id}
                className="group flex items-center justify-between py-2"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    {user.picture ? (
                      <AvatarImage
                        src={user.picture}
                        alt={user.name || user.email}
                      />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {getInitials(user.name, user.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {user.name || t('common.unknown')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemoveAccess(user)}
                  disabled={isRemoving}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Member Removal Confirmation Dialog */}
      {confirmRemoveUser && (
        <Dialog
          open={!!confirmRemoveUser}
          onOpenChange={() => setConfirmRemoveUser(null)}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t('access.remove.confirm.title', 'Remove team member?')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t(
                  'access.remove.confirm.description',
                  `Are you sure you want to remove ${confirmRemoveUser.name || confirmRemoveUser.email} from the team? Their files will remain in the team.`,
                )}
              </p>
              <div className="flex items-center gap-3 p-3 rounded-md bg-muted/50">
                <Avatar className="h-10 w-10">
                  {confirmRemoveUser.picture && (
                    <AvatarImage
                      src={confirmRemoveUser.picture}
                      alt={confirmRemoveUser.name || confirmRemoveUser.email}
                    />
                  )}
                  <AvatarFallback className="text-sm">
                    {getInitials(
                      confirmRemoveUser.name,
                      confirmRemoveUser.email,
                    )}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">
                    {confirmRemoveUser.name || t('common.unknown')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {confirmRemoveUser.email}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setConfirmRemoveUser(null)}
                  disabled={isRemoving}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmTeamRemoval}
                  disabled={isRemoving}
                >
                  {isRemoving
                    ? t('common.removing', 'Removing...')
                    : t('common.remove', 'Remove')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Folder Access Modal */}
      {folder && (
        <FolderAccessModal
          open={isFolderAccessModalOpen}
          onClose={() => {
            setIsFolderAccessModalOpen(false);
            setRefreshKey((prev) => prev + 1); // Refresh after closing
          }}
          folder={folder}
        />
      )}
    </TooltipProvider>
  );
};

export default AccessAvatarList;
